/**
 * Cooling — Input_Layer_Consolidation IC2.
 *
 * Single canonical CoolingType + `normaliseCooling` boundary projector
 * for the three legacy enum vocabularies in the codebase:
 *   - `ProjectIntent.coolingStrategy:      'air' | 'liquid' | 'hybrid' | 'undecided'`
 *   - `FeasibilityPreferences.coolingPreference:  'water' | 'dry' | 'hybrid' | 'agnostic'`
 *   - `WaterResourcePreferences.coolingType:      'wet' | 'hybrid' | 'dry' | 'tbd'`
 *
 * The audit (Input_Layer_Consolidation_Prompt.md §4) showed that the
 * three vocabularies describe the same physical concept but use
 * different words at every boundary. `normaliseCooling` projects all
 * nine legacy strings onto five canonical buckets so downstream code
 * (prompts, badges, scoring narratives, the diff tab) reasons about
 * one type instead of three.
 *
 * Migration discipline:
 *   - Writers continue to write to their existing fields (back-compat).
 *     On the next site-write the lazy migration normalises stored
 *     values, but the legacy fields stay.
 *   - Readers should route through `normaliseCooling` at the boundary.
 *     The legacy enum strings remain valid input for `normaliseCooling`
 *     so existing call sites can be migrated one at a time.
 *
 * Engineering rules (CLAUDE.md §4):
 *   - This file is pure types + pure projection. No I/O.
 */

/**
 * Canonical cooling strategy. Five buckets cover the physical reality:
 *
 *   - `air-dry-cooler`:     dry/air heat rejection — no water.
 *   - `evaporative`:        wet/water cooling — cooling tower or pond.
 *   - `closed-loop-liquid`: pumped liquid loops (direct-to-chip,
 *                            immersion, chilled water). Distinguished
 *                            from `evaporative` because the liquid is
 *                            internal — heat rejection at the cold
 *                            end is a separate question.
 *   - `hybrid`:             explicit hybrid combinations (e.g. wet
 *                            for hot days, dry for cooler weather).
 *   - `undecided`:          user hasn't picked. Default for new
 *                            intents and the projection of every
 *                            "agnostic" / "tbd" legacy value.
 */
export type CoolingType =
  | 'air-dry-cooler'
  | 'evaporative'
  | 'closed-loop-liquid'
  | 'hybrid'
  | 'undecided';

/**
 * Static projection table — every legacy enum string maps to exactly
 * one `CoolingType`. Keys are lower-cased so callers don't have to
 * pre-normalise; `normaliseCooling` lower-cases the input before lookup.
 *
 * Authoring contract:
 *   - Add an entry here in lockstep with extending any legacy enum.
 *   - `normaliseCooling` falls back to `'undecided'` for unknown
 *     strings — adding a new legacy value without updating this table
 *     silently regresses to undecided, which is detectable in the IC11
 *     coverage test.
 */
export const COOLING_ENUM_LEGACY_MAP: Readonly<Record<string, CoolingType>> = {
  // ProjectIntent.coolingStrategy vocabulary
  air: 'air-dry-cooler',
  liquid: 'closed-loop-liquid',
  hybrid: 'hybrid',
  undecided: 'undecided',
  // FeasibilityPreferences.coolingPreference vocabulary
  water: 'evaporative',
  dry: 'air-dry-cooler',
  agnostic: 'undecided',
  // WaterResourcePreferences.coolingType vocabulary
  wet: 'evaporative',
  tbd: 'undecided',
  // Self-mapping for already-canonical values — lets callers feed the
  // canonical type through `normaliseCooling` idempotently.
  'air-dry-cooler': 'air-dry-cooler',
  evaporative: 'evaporative',
  'closed-loop-liquid': 'closed-loop-liquid',
};

/**
 * Project any legacy or canonical cooling string onto the canonical
 * `CoolingType`. Pure. Unknown / empty inputs collapse to `'undecided'`
 * so downstream code doesn't have to null-check.
 *
 * @example
 *   normaliseCooling('liquid')   // 'closed-loop-liquid'
 *   normaliseCooling('Water')    // 'evaporative'
 *   normaliseCooling('agnostic') // 'undecided'
 *   normaliseCooling(undefined)  // 'undecided'
 */
export function normaliseCooling(raw: string | null | undefined): CoolingType {
  if (typeof raw !== 'string' || raw.length === 0) return 'undecided';
  const key = raw.trim().toLowerCase();
  return COOLING_ENUM_LEGACY_MAP[key] ?? 'undecided';
}

/** Human-readable label for the canonical type — used by IC4's
 *  buildInputLayersBlock and by the UI badges. */
export const COOLING_TYPE_LABEL: Readonly<Record<CoolingType, string>> = {
  'air-dry-cooler': 'Dry / air-cooled',
  evaporative: 'Evaporative (wet)',
  'closed-loop-liquid': 'Closed-loop liquid',
  hybrid: 'Hybrid',
  undecided: 'Undecided',
};
